import React from 'react';
import { SectionCard } from '../components/SectionCard';
import { LoadingBlock } from '../components/LoadingBlock';

export function DashboardPage({ loading, error, data }) {
  if (loading) return <LoadingBlock label="Carregando Central da Rodada..." />;
  if (error) return <div className="page-error">{error}</div>;

  const cards = data?.cards || {};
  const nextMatches = data?.charts?.next_matches || [];
  const featuredMatch = nextMatches[0] || null;
  const featuredScore = featuredMatch
    ? `${formatScore(featuredMatch.home_score)} x ${formatScore(featuredMatch.away_score)}`
    : 'vs';
  const liveMatches = Number(cards.live_matches ?? 0);
  const pendingOrders = Number(cards.pending_orders ?? 0);
  const openReports = Number(cards.open_reports ?? 0);
  const revenue = Number(cards.revenue_paid ?? 0);
  const operationAlerts = [
    liveMatches > 0
      ? `${liveMatches} partida${liveMatches > 1 ? 's' : ''} ao vivo pedindo cobertura.`
      : 'Nenhuma partida ao vivo neste momento.',
    pendingOrders > 0
      ? `${pendingOrders} pedido${pendingOrders > 1 ? 's' : ''} aguardando atenção da loja.`
      : 'Loja sem pedidos pendentes.',
    openReports > 0
      ? `${openReports} denúncia${openReports > 1 ? 's' : ''} aberta${openReports > 1 ? 's' : ''} na comunidade.`
      : 'Arquibancada sem denúncias abertas.',
  ];

  return (
    <div className="page-grid rodada-page">
      <section className="rodada-hero">
        <div className="rodada-hero__copy">
          <span className="eyebrow">Central da Liga</span>
          <h3>{featuredMatch ? `${featuredMatch.home_team_name} x ${featuredMatch.away_team_name}` : 'Rodada em preparação'}</h3>
          <p>
            {featuredMatch
              ? `${featuredMatch.campeonato_name} • ${formatMatchDate(featuredMatch.scheduled_at)} • ${featuredMatch.campo_name || 'campo a definir'}`
              : 'Quando a agenda receber uma partida, ela vira o centro da operação, transmissão, loja e comunidade.'}
          </p>
          <div className="rodada-scoreboard">
            <strong>{featuredMatch?.home_team_name || 'Mandante'}</strong>
            <span>{featuredScore}</span>
            <strong>{featuredMatch?.away_team_name || 'Visitante'}</strong>
          </div>
        </div>
        <div className="rodada-live-panel">
          <span className={`live-dot ${liveMatches > 0 ? 'is-live' : ''}`} />
          <strong>{liveMatches > 0 ? 'Transmissão ativa' : 'Central pronta'}</strong>
          <p>{liveMatches > 0 ? 'Acompanhe sala, eventos e operação em tempo real.' : 'A próxima partida já pode receber escalação, transmissão e torcida.'}</p>
        </div>
      </section>

      <div className="rodada-pulse-grid">
        <PulseCard label="Campeonatos" value={cards.championships ?? 0} hint="temporadas no ecossistema" />
        <PulseCard label="Times" value={cards.teams ?? 0} hint="clubes movimentando a liga" />
        <PulseCard label="Jogadores" value={cards.players ?? 0} hint="perfis prontos para escalação" />
        <PulseCard label="Jogos" value={cards.matches ?? 0} hint="partidas registradas" />
        <PulseCard label="Ao vivo" value={cards.live_matches ?? 0} hint="centrais em tempo real" highlight={liveMatches > 0} />
        <PulseCard label="Receita" value={`R$ ${revenue.toFixed(2)}`} hint="loja monetizando a comunidade" />
      </div>

      <div className="page-grid two-columns">
        <SectionCard title="Agenda quente" subtitle="Próximas centrais que podem virar transmissão, torcida e venda.">
          <div className="match-card-list">
            {nextMatches.length ? nextMatches.map((match) => (
              <article key={match.id} className="match-live-card">
                <div>
                  <span className="eyebrow">{match.status === 'live' ? 'Ao vivo agora' : match.campeonato_name}</span>
                  <strong>{match.home_team_name} x {match.away_team_name}</strong>
                  <p>{formatMatchDate(match.scheduled_at)} • {match.phase || 'Rodada'} {match.round_number ? match.round_number : ''}</p>
                </div>
                <span className={`status-pill ${match.status === 'live' ? 'is-live' : ''}`}>{match.status === 'live' ? 'AO VIVO' : 'AGENDADO'}</span>
              </article>
            )) : <div className="empty-state">Sem jogos programados. A rodada aparece aqui assim que a agenda for criada.</div>}
          </div>
        </SectionCard>

        <SectionCard title="Alertas da operação" subtitle="O que precisa de atenção antes da bola rolar.">
          <div className="operation-feed">
            {operationAlerts.map((item, index) => (
              <article key={item} className="operation-feed__item">
                <span>{index + 1}</span>
                <p>{item}</p>
              </article>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="callout-grid rodada-ecosystem">
        <div className="callout-box">
          <span className="eyebrow">Arquibancada</span>
          <strong>Comunidade conectada à partida</strong>
          <p>As salas de jogo, time e campeonato deixam de ser chat solto e passam a sustentar a narrativa da rodada.</p>
        </div>
        <div className="callout-box">
          <span className="eyebrow">Monetização local</span>
          <strong>Loja e cupons dentro do contexto esportivo</strong>
          <p>Produtos e descontos ganham força quando aparecem ligados ao time, ao jogo e aos destaques da rodada.</p>
        </div>
      </div>
    </div>
  );
}

function PulseCard({ label, value, hint, highlight = false }) {
  return (
    <article className={`pulse-card ${highlight ? 'is-hot' : ''}`}>
      <span className="eyebrow">{label}</span>
      <strong>{value}</strong>
      <p>{hint}</p>
    </article>
  );
}

function formatMatchDate(value) {
  if (!value) return 'data a definir';
  return String(value).replace('T', ' ').slice(0, 16);
}

function formatScore(value) {
  const score = Number(value ?? 0);
  return Number.isFinite(score) ? score : 0;
}
